import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import apiService from '../services/apiService';

const EmailConfigModal = ({ open, onClose, reportType = 'daily', applicationName = null }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const [emailConfig, setEmailConfig] = useState({
    recipients: [], 
    cc_recipients: [], 
    subject: '',
    content: '',
    enabled: false,
    includeImage: true,
    imageFormat: 'jpeg'
  });

  const [newEmail, setNewEmail] = useState('');
  const [newCcEmail, setNewCcEmail] = useState('');

  // Format report type for display (remove underscores, capitalize words)
  const formatReportType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Load email configuration
  useEffect(() => {
    if (open) {
      loadEmailConfig();
      setIsEditing(false); // Reset edit mode when opening
    }
  }, [open]);

  const loadEmailConfig = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('📥 Loading email configuration for report type:', reportType);
      const data = await apiService.get('/email/config');
      console.log('📥 Received email config data:', JSON.stringify(data, null, 2));
      
      // Normalize reportType: convert boomi_daily to daily, boomi_weekly to weekly, etc.
      let normalizedReportType = reportType;
      if (reportType.startsWith('boomi_')) {
        normalizedReportType = reportType.replace('boomi_', '');
      }
      
      console.log('📥 Normalized report type:', normalizedReportType);
      console.log('📥 Available report types in data:', Object.keys(data));
      
      // Load only the configuration for the current report type (using normalized type)
      const config = data[normalizedReportType] || {
        recipients: [],
        cc_recipients: [],
        subject: '',
        content: '',
        enabled: false,
        includeImage: true,
        imageFormat: 'jpeg'
      };
      
      console.log('📥 Loaded config for', normalizedReportType, ':', JSON.stringify(config, null, 2));
      
      // Check if configuration exists (has recipients)
      const configExists = config.recipients && config.recipients.length > 0;
      setHasExistingConfig(configExists);
      setIsEditing(!configExists); // If no config exists, start in edit mode
      
      console.log('📥 Config exists:', configExists, 'Is editing:', !configExists);
      console.log('📥 Recipients found:', config.recipients);
      
      setEmailConfig({
        recipients: config.recipients || [],
        cc_recipients: config.cc_recipients || [],
        subject: config.subject || '',
        content: config.content || '',
        enabled: config.enabled || false,
        includeImage: config.includeImage !== false,
        imageFormat: config.imageFormat || 'jpeg'
      });
      
      console.log('✅ Email config loaded successfully');
    } catch (error) {
      console.error('❌ Error loading email config:', error);
      setError('Failed to load email configuration');
      setIsEditing(true); // Start in edit mode if loading fails
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addRecipient = (type) => {
    const email = type === 'cc' ? newCcEmail : newEmail;
    if (!email.trim()) return;

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const field = type === 'cc' ? 'cc_recipients' : 'recipients';
    const currentEmails = emailConfig[field];

    if (currentEmails.includes(email)) {
      setError('Email address already exists');
      return;
    }

    setEmailConfig(prev => ({
      ...prev,
      [field]: [...currentEmails, email]
    }));

    if (type === 'cc') {
      setNewCcEmail('');
    } else {
      setNewEmail('');
    }
    setError('');
  };

  const removeRecipient = (type, emailToRemove) => {
    const field = type === 'cc' ? 'cc_recipients' : 'recipients';
    setEmailConfig(prev => ({
      ...prev,
      [field]: prev[field].filter(email => email !== emailToRemove)
    }));
  };

  const updateConfigField = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveConfiguration = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate that at least one recipient is configured
      if (!emailConfig.recipients || emailConfig.recipients.length === 0) {
        setError('Please add at least one recipient');
        setSaving(false);
        return;
      }

      // Normalize reportType: convert boomi_daily to daily, boomi_weekly to weekly, etc.
      let normalizedReportType = reportType;
      if (reportType.startsWith('boomi_')) {
        normalizedReportType = reportType.replace('boomi_', '');
      }

      console.log('💾 Saving email configuration:', {
        reportType: reportType,
        normalizedReportType: normalizedReportType,
        recipients: emailConfig.recipients,
        cc_recipients: emailConfig.cc_recipients,
        enabled: emailConfig.enabled,
        subject: emailConfig.subject,
        content: emailConfig.content
      });

      // Save recipients AND enabled status to ALL report types
      const reportTypes = ['daily', 'weekly', 'monthly'];
      const recipientPromises = reportTypes.map(type => {
        const payload = {
          reportType: type,
          recipients: emailConfig.recipients,
          ccRecipients: emailConfig.cc_recipients,
          // Save enabled status to all reports
          enabled: emailConfig.enabled,
          // Don't update subject/content for other report types
          subject: '',
          content: '',
          includeImage: emailConfig.includeImage,
          imageFormat: emailConfig.imageFormat
        };
        console.log(`📤 Saving to ${type}:`, payload);
        return apiService.post('/email/config', payload);
      });

      await Promise.all(recipientPromises);

      // Save subject/content ONLY for current report type
      const currentPayload = {
        reportType: normalizedReportType,
        recipients: emailConfig.recipients,
        ccRecipients: emailConfig.cc_recipients,
        subject: emailConfig.subject,
        content: emailConfig.content,
        enabled: emailConfig.enabled,
        includeImage: emailConfig.includeImage,
        imageFormat: emailConfig.imageFormat
      };

      console.log(`📤 Saving full config to ${normalizedReportType}:`, currentPayload);
      await apiService.post('/email/config', currentPayload);

      const enabledStatus = emailConfig.enabled ? 'ENABLED' : 'DISABLED';
      setSuccess(`✅ Recipients and automatic email (${enabledStatus}) saved for ALL reports. Subject and content saved for ${formatReportType(reportType)}.`);
      setHasExistingConfig(true);
      setIsEditing(false);
      
      console.log('✅ Save completed successfully');
      
      // Reload configuration to verify it was saved
      setTimeout(() => {
        loadEmailConfig();
      }, 500);
      
      // Auto-close after 3 seconds on success
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('❌ Error saving email config:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save email configuration';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    loadEmailConfig(); // Reload original config
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setNewEmail('');
    setNewCcEmail('');
    setIsEditing(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              Email Configuration - {applicationName || formatReportType(reportType)}
            </Typography>
          </Box>
          {hasExistingConfig && !isEditing && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleEdit}
              startIcon={<SettingsIcon />}
            >
              Edit
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Email Draft Style */}
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, bgcolor: isEditing ? 'white' : '#f9f9f9' }}>
              {/* TO Recipients */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 600, color: '#666' }}>
                    To
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {isEditing && (
                      <Box display="flex" gap={1} mb={1}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add recipients"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addRecipient('to')}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                        />
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addRecipient('to')}
                          disabled={!newEmail.trim()}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {emailConfig?.recipients?.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={isEditing ? () => removeRecipient('to', email) : undefined}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                      {!isEditing && emailConfig?.recipients?.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No recipients</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* CC Recipients */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 600, color: '#666' }}>
                    Cc
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {isEditing && (
                      <Box display="flex" gap={1} mb={1}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add CC recipients"
                          value={newCcEmail}
                          onChange={(e) => setNewCcEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addRecipient('cc')}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                        />
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addRecipient('cc')}
                          disabled={!newCcEmail.trim()}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {emailConfig?.cc_recipients?.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={isEditing ? () => removeRecipient('cc', email) : undefined}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ))}
                      {!isEditing && emailConfig?.cc_recipients?.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No CC recipients</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Subject */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 600, color: '#666' }}>
                    Subject
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Email subject"
                    value={emailConfig?.subject || ''}
                    onChange={(e) => updateConfigField('subject', e.target.value)}
                    variant="standard"
                    disabled={!isEditing}
                    InputProps={{ 
                      disableUnderline: true,
                      sx: { fontSize: '0.95rem' }
                    }}
                  />
                </Box>
              </Box>

              {/* Content */}
              <Box sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  placeholder="Compose email content (optional)..."
                  value={emailConfig?.content || ''}
                  onChange={(e) => updateConfigField('content', e.target.value)}
                  variant="standard"
                  disabled={!isEditing}
                  InputProps={{ 
                    disableUnderline: true,
                    sx: { fontSize: '0.95rem', lineHeight: 1.6 }
                  }}
                />
              </Box>
            </Box>

            {/* Additional Settings */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ color: '#1976d2' }}>
                Email Settings
              </Typography>
              
              {/* Enable/Disable Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={emailConfig?.enabled || false}
                    onChange={(e) => updateConfigField('enabled', e.target.checked)}
                    color="primary"
                    disabled={!isEditing}
                  />
                }
                label={
                  <Typography variant="body2">
                    {emailConfig?.enabled 
                      ? "✅ Automatic emails enabled" 
                      : "⚠️ Automatic emails disabled"}
                  </Typography>
                }
                sx={{ mb: 1 }}
              />

              {/* Image Attachment */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailConfig?.includeImage !== false}
                      onChange={(e) => updateConfigField('includeImage', e.target.checked)}
                      color="primary"
                      disabled={!isEditing}
                    />
                  }
                  label={<Typography variant="body2">Include report image as attachment</Typography>}
                />
                
                {emailConfig?.includeImage !== false && (
                  <FormControl size="small" sx={{ ml: 4, mt: 1, minWidth: 200 }} disabled={!isEditing}>
                    <InputLabel>Image Format</InputLabel>
                    <Select
                      value={emailConfig?.imageFormat || 'jpeg'}
                      onChange={(e) => updateConfigField('imageFormat', e.target.value)}
                      label="Image Format"
                    >
                      <MenuItem value="jpeg">JPEG (Smaller size)</MenuItem>
                      <MenuItem value="png">PNG (Higher quality)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          {isEditing ? 'Cancel' : 'Close'}
        </Button>
        {isEditing && hasExistingConfig && (
          <Button onClick={handleCancelEdit} disabled={saving}>
            Cancel Edit
          </Button>
        )}
        {isEditing && (
          <Button
            variant="contained"
            onClick={saveConfiguration}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailConfigModal;
